import { z } from "zod";
const unmappedSource = z.object({
  divisionCode: z.string().nullable(),
  productCode: z.string().nullable(),
});
const amount = z.number().finite().nullable();
export const OpeningBookCatalogSchema = z.object({
  products: z.array(
    z.object({ companyCode: z.string(), divisionCode: z.string(), productCode: z.string() }),
  ),
  issues: z.array(z.string()),
  unmappedProducts: z
    .array(
      z.object({
        companyCode: z.string(),
        divisionCode: z.string(),
        productCode: z.string(),
        unmappedSource,
      }),
    )
    .default([]),
});
export const OpeningBookPreviewSchema = z.object({
  snapshotId: z.string().uuid(),
  unmappedSource: unmappedSource.nullish(),
  sourceOpeningBalance: amount.optional(),
  normalizationPolicy: z.literal("NEGATIVE_OPENING_ZERO_FLOOR_V1").nullish(),
  adjustments: z
    .array(
      z.object({
        sourceKey: z.string(),
        lobCode: z.string(),
        stateCode: z.string(),
        premiumType: z.string(),
        sourceOpening: z.number().finite(),
        planningOpening: z.literal(0),
        adjustment: z.number().finite(),
      }),
    )
    .optional(),
  status: z.enum(["LOADING", "READY", "FAILED", "PUBLISHED"]),
  openingRequirement: z
    .enum(["OPENING_BOOK_REQUIRED", "ZERO_RUNOFF_CONFIRMED"])
    .default("OPENING_BOOK_REQUIRED"),
  coordinate: z.object({
    companyCode: z.string(),
    divisionCode: z.string(),
    productCode: z.string(),
  }),
  valuationDate: z.string(),
  budgetStartPeriod: z.string(),
  buildId: z.string(),
  basisKind: z.string(),
  extractedAt: z.string(),
  expectedPublishedId: z.string().uuid().nullable(),
  bridgeChecksum: z.string(),
  issues: z.array(z.string()),
  companyCoverageIssues: z.array(z.string()).default([]),
  bridgePeriods: z.array(z.string()),
  bridge: z.array(
    z.object({ period: z.string(), lobCode: z.string(), writtenPremium: z.number() }),
  ),
  openingAtBudget: amount,
  bridgeOpeningUep: amount,
  stateWarning: z.string().nullable(),
  financialStatementRequirement: z.string(),
  recomputeRequired: z.array(z.string()).optional(),
  rollforward: z
    .array(
      z.object({
        period: z.string(),
        beginningUep: amount,
        forecastWrittenPremium: amount,
        fiarEarnedPremium: amount,
        bridgeEarnedPremium: amount,
        forecastEarnedPremium: amount,
        totalEarnedPremium: amount,
        closingUep: amount,
        persistedEarnedPremium: amount,
        difference: amount,
        complete: z.boolean(),
      }),
    )
    .optional(),
  rows: z.array(
    z.object({
      sourceKey: z.string(),
      lobCode: z.string(),
      stateCode: z.string(),
      premiumType: z.string(),
      opening: amount,
      fullFutureEarned: amount,
      sourceStatus: z.string(),
      sourceExceptionCount: z.number(),
      mappingExceptionCount: z.number(),
      stateExceptionCount: z.number(),
      sourceTransactionCount: z.number(),
      sourceAsOf: z.string(),
      months: z.array(
        z.object({ period: z.string(), beginning: amount, earned: amount, closing: amount }),
      ),
    }),
  ),
});
export type OpeningBookPreview = z.infer<typeof OpeningBookPreviewSchema>;
